interface DataPreview {
  headers: string[];
  rows: string[][];
  detectedTypes: Record<string, string>;
}

// Transform detected types into schema column format
export const createSchemaFromPreview = (
  preview: DataPreview,
  actualDatasetId: string,
  delimiter?: string,
) => {
  const columns = preview.headers
    .filter((header) => header && header.trim().length > 0)
    .map((header, index) => ({
      name: header.trim(),
      type: preview.detectedTypes[header] || 'string',
      nullable: true,
      pos: index + 1,
      source: header.trim(),
    }));

  return {
    datasetId: actualDatasetId,
    columns,
    status: 'current' as const,
    ...(delimiter && { parsingOptions: { delimiter } }),
  };
};

// Auto-detect CSV delimiter
export const detectDelimiter = (text: string): string => {
  const sample = text.split('\n').slice(0, 5).join('\n'); // Check first 5 lines
  const delimiters = [',', ';', '\t', '|'];
  const counts = delimiters.map((delim) => ({
    delimiter: delim,
    count: (sample.match(new RegExp(`\\${delim}`, 'g')) || []).length,
  }));

  // Return the delimiter with the highest count
  const best = counts.reduce((prev, curr) =>
    curr.count > prev.count ? curr : prev,
  );
  return best.count > 0 ? best.delimiter : ',';
};

// Proper CSV parser that handles quoted fields with newlines and commas
export const parseCSV = (text: string, delimiter?: string): string[][] => {
  const actualDelimiter = delimiter || detectDelimiter(text);
  const result: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote inside quoted field
        currentField += '"';
        i += 2;
        continue;
      } else {
        // Start or end of quoted field
        inQuotes = !inQuotes;
      }
    } else if (char === actualDelimiter && !inQuotes) {
      // Field separator
      currentRow.push(currentField.trim());
      currentField = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      // Row separator
      if (currentField || currentRow.length > 0) {
        currentRow.push(currentField.trim());
        if (currentRow.some((field) => field.length > 0)) {
          result.push(currentRow);
        }
        currentRow = [];
        currentField = '';
      }
      // Skip \r\n sequence
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
    } else {
      currentField += char;
    }
    i++;
  }

  // Handle last field/row
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some((field) => field.length > 0)) {
      result.push(currentRow);
    }
  }

  return result;
};

export const dataPreviewFromFile = async (
  file: File,
  fileType: 'csv' | 'xlsx',
): Promise<DataPreview> => {
  if (fileType === 'csv') {
    const text = await file.text();
    const parsedRows = parseCSV(text);

    if (parsedRows.length === 0) {
      throw new Error('No data found in CSV file');
    }

    if (parsedRows.length === 1) {
      throw new Error('Only header row found, no data rows detected');
    }

    if (parsedRows[0].length === 1) {
      throw new Error(
        'Only one column detected. Check if the delimiter is correct.',
      );
    }

    const headers = parsedRows[0] || [];
    const rows = parsedRows.slice(1, 6); // Show first 5 data rows

    // Enhanced type detection
    const detectedTypes: Record<string, string> = {};
    headers.forEach((header, index) => {
      const sampleValues = rows
        .map((row) => row[index])
        .filter((val) => val && val.trim() !== '')
        .slice(0, 10); // Check first 10 non-empty values

      if (sampleValues.length === 0) {
        detectedTypes[header] = 'string';
        return;
      }

      // Check for boolean values
      const booleanValues = ['true', 'false', 'yes', 'no', '1', '0'];
      const isBooleanLike = sampleValues.every((val) =>
        booleanValues.includes(val.toLowerCase().trim()),
      );

      if (isBooleanLike) {
        detectedTypes[header] = 'boolean';
        return;
      }

      // Check for numeric values (including decimals)
      const isNumeric = sampleValues.every((val) => {
        const num = parseFloat(val.replace(/[,$%]/g, ''));
        return !isNaN(num) && isFinite(num);
      });

      if (isNumeric) {
        // Distinguish between integer and float
        const hasDecimals = sampleValues.some((val) => {
          const cleaned = val.replace(/[,$%]/g, '');
          return cleaned.includes('.') && !cleaned.endsWith('.0');
        });
        detectedTypes[header] = hasDecimals ? 'float' : 'integer';
        return;
      }

      // Check for date values
      const isDate = sampleValues.some((val) => {
        const date = new Date(val);
        return !isNaN(date.getTime()) && val.length > 4; // Avoid detecting years as dates
      });

      if (isDate) {
        detectedTypes[header] = 'datetime';
        return;
      }

      // Default to string
      detectedTypes[header] = 'string';
    });

    return {
      headers,
      rows,
      detectedTypes,
    };
  }

  // For Excel files, provide basic info
  return {
    headers: ['Excel file detected'],
    rows: [['Full preview available after upload']],
    detectedTypes: {},
  };
};
